import React from "react";
import { Link } from "react-router-dom";
import { AlertMessage } from "components/atoms";
import { DragDropUpload } from "components/molecules";
import { useUserData, useUpload, useFileValidation } from "hooks";
import { convertDocumentTypeToEnumValue, fileTypeOptionsGrouped } from "types";
import "./UploadDocumentPage.scss";
import { documentService } from "services";

const ALLOWED_KEYS: Record<string, Set<string>> = {
  extractedData: new Set([
    "personalInformation",
    "englishLanguageTestScores",
    "highSchool",
    "tertiaryEducation1",
    "tertiaryEducation2",
    "tertiaryEducation3",
    "documents",
    "profilePictureUrl",
    "employment1",
    "employment2",
    "employment3",
  ]),

  personalInformation: new Set([
    "basicDetails",
    "contactDetails",
    "passportDetails",
    "maritalStatus",
    "registeredDateOfMarriage",
  ]),

  basicDetails: new Set([
    "title",
    "firstName",
    "middleName",
    "lastName",
    "dateOfBirth",
    "gender",
    "countryOfBirth",
    "countryOfCitizenship",
    "isCitizenshipSameAsBirth",
  ]),

  contactDetails: new Set([
    "phone",
    "mobile",
    "email",
    "currentAddress",
    "homeAddress",
    "emergencyContactName",
    "emergencyContactRelationship",
    "emergencyContactPhone",
    "emergencyContactMobile",
  ]),

  passportDetails: new Set([
    "passportNumber",
    "passportExpiryDate",
    "passportIssuingCountry",
    "isPassportSameAsBirth",
  ]),

  phone: new Set(["countryCode", "areaCode", "number", "extension"]),

  mobile: new Set(["countryCode", "areaCode", "number", "extension"]),

  currentAddress: new Set([
    "street1",
    "street2",
    "city",
    "state",
    "postalCode",
    "country",
  ]),

  homeAddress: new Set([
    "street1",
    "street2",
    "city",
    "state",
    "postalCode",
    "country",
  ]),

  emergencyContactPhone: new Set([
    "countryCode",
    "areaCode",
    "number",
    "extension",
  ]),

  emergencyContactMobile: new Set([
    "countryCode",
    "areaCode",
    "number",
    "extension",
  ]),

  englishLanguageTestScores: new Set([
    "englishTest",
    "overallScore",
    "reading",
    "writing",
    "speaking",
    "listening",
    "useOfEnglish",
    "dateOfCompletion",
    "testReportFormNumber",
  ]),

  highSchool: new Set([
    "school",
    "city",
    "country",
    "gradeObtained",
    "startDate",
    "endDate",
    "isStillAttending",
  ]),

  tertiaryEducation1: new Set([
    "school",
    "course",
    "city",
    "country",
    "gradeObtained",
    "startDate",
    "endDate",
    "isStillAttending",
  ]),

  tertiaryEducation2: new Set([
    "school",
    "course",
    "city",
    "country",
    "gradeObtained",
    "startDate",
    "endDate",
    "isStillAttending",
  ]),

  tertiaryEducation3: new Set([
    "school",
    "course",
    "city",
    "country",
    "gradeObtained",
    "startDate",
    "endDate",
    "isStillAttending",
  ]),

  employment1: new Set([
    "companyName",
    "positionAndDuties",
    "dateCommenced",
    "dateEnded",
    "isStillWorking",
  ]),

  employment2: new Set([
    "companyName",
    "positionAndDuties",
    "dateCommenced",
    "dateEnded",
    "isStillWorking",
  ]),

  employment3: new Set([
    "companyName",
    "positionAndDuties",
    "dateCommenced",
    "dateEnded",
    "isStillWorking",
  ]),
};

interface UploadDocumentPageProps {
  hideTitle?: boolean;
  linkApplication?: {
    applicationId: string;
    institutionId: string;
    onSuccess?: () => void;
  };
}

const UploadDocumentPage: React.FC<UploadDocumentPageProps> = ({
  hideTitle = false,
  linkApplication,
}) => {
  const sectionOrder = [
    "personalInformation.basicDetails",
    "personalInformation.contactDetails",
    "personalInformation.passportDetails",
    "personalInformation.visaDetails",
    "personalInformation",
    "highSchool",
    "tertiaryEducation1",
    "tertiaryEducation2",
    "tertiaryEducation3",
    "employment1",
    "employment2",
    "employment3",
    "englishLanguageTestScores",
    "studyPreference",
    "scholarshipFinancialSupport",
    "healthInsurance",
  ];

  const userDataHook = useUserData();

  const { validateFileSync, getAcceptedFileTypes, getMaxFileSize } =
    useFileValidation({
      maxFileSize: 5 * 1024 * 1024, // 5MB
      enableMagicNumberValidation: false, // Disabled for sync validation
      sanitizeMetadata: true,
    });

  const {
    stage,
    uploadProgress,
    extractionProgress,
    updateProgress,
    successMessage,
    errorMessage,
    handleFileUpload,
    handleDataExtraction,
    handleApplyData,
    handleFileRemove,
    handleError,
    getFileStage,
  } = useUpload(userDataHook, ALLOWED_KEYS, {
    enableExtraction: true,
    enableFileValidation: true,
  });

  return (
    <div className="form-group document-upload-page">
      {!hideTitle && (
        <div className="form-group">
          <h2>Upload Documents</h2>
        </div>
      )}

      <div className="upload-instructions">
        <p>Before You Upload:</p>
        <ul>
          <li>Supported formats: PDF, DOC, DOCX, JPG, PNG (Max size: 5MB)</li>
          <li>Ensure your documents are clear and all text is legible</li>
          <li>Select the correct document type</li>
          <li>Upload files, extract data and review before clicking Confirm</li>
          <li>
            <strong>Security:</strong> All files undergo comprehensive
            validation including file signature verification
          </li>
        </ul>
      </div>

      <div className="form-group">
        <DragDropUpload
          onError={handleError}
          onFileUpload={handleFileUpload}
          onDataExtraction={handleDataExtraction}
          onApplyData={handleApplyData}
          onFileRemove={handleFileRemove}
          validateFile={validateFileSync}
          acceptedFileTypes={getAcceptedFileTypes()}
          fileTypeOptions={fileTypeOptionsGrouped()}
          maxFileSize={getMaxFileSize()}
          sectionOrder={sectionOrder}
          stage={{
            ...stage,
            ...Object.fromEntries(
              Object.entries(extractionProgress).map(([key, value]) => [
                key,
                value < 100 ? "extracting" : "complete",
              ]),
            ),
          }}
          uploadProgress={{
            ...uploadProgress,
            ...extractionProgress,
            ...updateProgress,
          }}
          onAttachToApplication={
            linkApplication?.applicationId && linkApplication?.institutionId
              ? async (documentTypes: string[]) => {
                  await documentService.linkDocumentsToApplication(
                    linkApplication.institutionId,
                    linkApplication.applicationId,
                    documentTypes.map(convertDocumentTypeToEnumValue),
                  );
                  linkApplication.onSuccess?.();
                }
              : undefined
          }
        />
        {Object.keys(stage).length > 0 && (
          <div className="processing-status">
            {Object.entries(stage).map(([fileName, _]) => {
              const currentStage = getFileStage(fileName);
              const progress =
                currentStage === "extracting"
                  ? extractionProgress[fileName]
                  : uploadProgress[fileName];

              return (
                <div key={fileName} className="status-item">
                  <span className="file-name">{fileName}</span>
                  <span className={`status-label ${currentStage}`}>
                    {currentStage}
                  </span>
                  {progress !== undefined && (
                    <span className="progress-percent">
                      ({Math.round(progress)}%)
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {successMessage && (
        <AlertMessage type="success">{successMessage}</AlertMessage>
      )}
      {errorMessage && <AlertMessage type="error">{errorMessage}</AlertMessage>}

      <small>
        We securely store your information. See our{" "}
        <Link to="/underConstruction" className="small">
          Data Policy
        </Link>{" "}
        for details.
      </small>
    </div>
  );
};

export default UploadDocumentPage;
